import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Building2,
  MapPin,
  Truck as TruckIcon,
  Loader2,
  ArrowRight,
  CheckCircle2,
  Plug,
  SlidersHorizontal,
  Warehouse,
  Plus,
  Trash2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import LocationPicker from "../components/LocationPicker";
import OnboardingPartnersPanel from "../components/OnboardingPartnersPanel";
import OnboardingOperationsPanel from "../components/OnboardingOperationsPanel";
import { useAuth } from "../lib/auth";
import { fetchOnboardingState, saveOnboardingEssentials } from "../lib/api";

/**
 * Onboarding wizard — Phase 1 + Phase 2 (Feb 2026).
 *
 *  Phase 1 (Essentials): Business letterhead · Depot · First truck
 *  Phase 2 (Partners):   AA · First Rescue · Telematics Guru · Email intake
 *
 * Admin-only. Phases are independently resumable; Phase 2 partners
 * each have Skip-for-now so a tenant without AA can still finish.
 */

// Combined step list. First three are the Phase-1 fields; fourth is
// Phase-2 partner picker; fifth is Phase-3 operations defaults.
const STEPS = [
  { k: "business", icon: Building2, label: "Business", phase: 1 },
  { k: "depot", icon: MapPin, label: "Depot", phase: 1 },
  { k: "yards", icon: Warehouse, label: "Yards", phase: 1 },
  { k: "truck", icon: TruckIcon, label: "First Truck", phase: 1 },
  { k: "partners", icon: Plug, label: "Integrations", phase: 2 },
  { k: "operations", icon: SlidersHorizontal, label: "Operations", phase: 3 },
];

const emptyForm = {
  business: {
    trading_name: "",
    trading_as: "",
    address_line1: "",
    address_line2: "",
    phone_primary: "",
    phone_secondary: "",
    email: "",
    gst_number: "",
  },
  depot: { address: "", lat: null, lng: null },
  yards: [{ name: "Main yard", address: "", lat: null, lng: null }],
  truck: { name: "", driver: "", phone: "", capacity: "Standard Tow" },
};

export default function OnboardingPage() {
  const nav = useNavigate();
  const { user, isAdmin } = useAuth();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadState = () =>
    fetchOnboardingState()
      .then((s) => {
        setState(s);
        // Jump to the earliest unfinished step on load so Jeremy
        // doesn't scroll past 3 already-done screens every time.
        if (s.phase3_operations !== "done" && s.phase2_integrations === "done") {
          setStep(5);
        } else if (s.phase1_essentials === "done") {
          setStep(4);
        }
      })
      .catch(() => toast.error("Failed to load onboarding progress"));

  useEffect(() => {
    // Guard against the auth interceptor race — AuthProvider attaches
    // the axios Authorization header inside its own useEffect, which
    // runs AFTER child effects. Firing fetchOnboardingState() here on
    // a cold page load (reload / direct nav / signup redirect) hits
    // the API with no bearer token and 401s. Wait until `user` is
    // either loaded (object) or confirmed-missing (false) before
    // deciding what to do.
    if (user === null) return; // still resolving
    if (user === false) {
      nav("/login");
      return;
    }
    loadState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const upd = (section) => (k, v) =>
    setForm((f) => ({ ...f, [section]: { ...f[section], [k]: v } }));

  const stepValid = useMemo(() => {
    const b = form.business;
    const t = form.truck;
    const p1done = state?.phase1_essentials === "done";
    return [
      // Business — pre-filled valid if Phase 1 is already done.
      p1done || !!(b.trading_name && b.address_line1 && b.phone_primary && b.email),
      // Depot — needs an address AND a resolved pin.
      p1done || !!(form.depot.address && form.depot.lat != null),
      // Yards — optional; a tenant with no storage yard can skip straight past.
      true,
      // Truck.
      p1done || !!(t.name && t.driver),
      // Partners step — always valid (skip is always available).
      true,
    ];
  }, [form, state]);

  // Phase-1 "finish" button only shows on the truck step. Phases 2/3
  // use their own card buttons; the wizard is complete when the
  // backend reports all phases done.
  const atPartnersStep = step === 4;
  const atOperationsStep = step === 5;
  const canFinishEssentials = stepValid[0] && stepValid[1] && stepValid[3];
  const allDone =
    state?.phase1_essentials === "done" &&
    state?.phase2_integrations === "done" &&
    state?.phase3_operations === "done";

  const saveEssentialsAndAdvance = async () => {
    setSaving(true);
    try {
      const yards = (form.yards || []).filter((y) => y.name && y.address);
      const r = await saveOnboardingEssentials({ ...form, yards });
      setState(r.state);
      toast.success("Essentials saved — now hook up your partners.");
      setStep(4);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save essentials");
    } finally {
      setSaving(false);
    }
  };

  const finishWizard = () => {
    toast.success("You're all set — welcome aboard!");
    setTimeout(() => nav("/"), 400);
  };

  if (user === null) {
    // AuthProvider still resolving — render nothing to avoid flashing
    // the admin-only lockout screen below.
    return null;
  }

  if (user === false) {
    nav("/login");
    return null;
  }

  if (user && !isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-300 p-6">
        <div className="max-w-md text-center space-y-3">
          <h1 className="text-xl font-bold text-slate-100">
            Only your account admin can finish setup.
          </h1>
          <p className="text-sm">
            Ask them to log in — the "Finish setup" banner will appear on their
            dashboard.
          </p>
          <Button onClick={() => nav("/")}>Back to dashboard</Button>
        </div>
      </div>
    );
  }

  // Already done — just show a celebratory confirmation + CTA.
  if (state?.is_complete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-100 p-6">
        <div className="max-w-md text-center space-y-4" data-testid="onboarding-complete">
          <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto" />
          <h1 className="text-2xl font-bold">You're already set up</h1>
          <p className="text-sm text-slate-400">
            Later phases (partner integrations, pricing, branding) are live on
            Settings → Integrations.
          </p>
          <Button onClick={() => nav("/")} data-testid="onboarding-open-dashboard">
            Open Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-slate-950 text-slate-100 py-8 px-4"
      data-testid="onboarding-page"
    >
      <div className="max-w-2xl mx-auto space-y-6">
        <header className="text-center space-y-2">
          <h1 className="text-3xl font-black tracking-tight">
            Let's get Tow-Hook running
          </h1>
          <p className="text-sm text-slate-400">
            Business, depot, your yards and your first truck. You can tune
            everything later from Settings.
          </p>
        </header>

        {/* Progress rail */}
        <div className="flex items-center gap-2" data-testid="onboarding-rail">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const done = stepValid[i];
            const active = step === i;
            return (
              <button
                key={s.k}
                onClick={() => setStep(i)}
                data-testid={`onboarding-step-${s.k}`}
                className={
                  "flex-1 px-3 py-2.5 rounded-md flex items-center gap-2 text-sm font-semibold transition-colors " +
                  (active
                    ? "bg-emerald-600 text-white"
                    : done
                      ? "bg-emerald-900/40 text-emerald-200 hover:bg-emerald-900/60"
                      : "bg-slate-800 text-slate-400 hover:bg-slate-700")
                }
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="truncate">{s.label}</span>
                {done && <CheckCircle2 className="h-3.5 w-3.5 ml-auto" />}
              </button>
            );
          })}
        </div>

        <section className="rounded-lg border border-slate-800 bg-slate-900 p-5 space-y-4">
          {step === 0 && <BusinessStep form={form.business} upd={upd("business")} />}
          {step === 1 && <DepotStep value={form.depot} onChange={(v) => setForm((f) => ({ ...f, depot: v }))} />}
          {step === 2 && (
            <YardsStep
              yards={form.yards}
              onChange={(y) => setForm((f) => ({ ...f, yards: y }))}
              depot={form.depot}
            />
          )}
          {step === 3 && <TruckStep form={form.truck} upd={upd("truck")} />}
          {step === 4 && state && (
            <OnboardingPartnersPanel state={state} onChange={loadState} />
          )}
          {step === 5 && state && (
            <OnboardingOperationsPanel state={state} onChange={loadState} />
          )}
        </section>

        {/* Footer */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
            data-testid="onboarding-back-btn"
          >
            Back
          </Button>
          {step < 3 && (
            <Button
              onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
              disabled={!stepValid[step]}
              data-testid="onboarding-next-btn"
            >
              Next
              <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          )}
          {step === 3 && (
            <Button
              onClick={saveEssentialsAndAdvance}
              disabled={!canFinishEssentials || saving}
              className="bg-emerald-600 hover:bg-emerald-500"
              data-testid="onboarding-finish-essentials-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                  Saving…
                </>
              ) : (
                <>
                  Save &amp; next
                  <ArrowRight className="h-4 w-4 ml-1.5" />
                </>
              )}
            </Button>
          )}
          {atPartnersStep && (
            <Button
              onClick={() => setStep(5)}
              disabled={state?.phase2_integrations !== "done"}
              className="bg-emerald-600 hover:bg-emerald-500"
              data-testid="onboarding-partners-next-btn"
              title={
                state?.phase2_integrations === "done"
                  ? "Next: Operations"
                  : "Set up or skip every partner first"
              }
            >
              Next: Operations
              <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          )}
          {atOperationsStep && (
            <Button
              onClick={finishWizard}
              disabled={!allDone}
              className="bg-emerald-600 hover:bg-emerald-500"
              data-testid="onboarding-finish-btn"
              title={
                allDone
                  ? "Open dashboard"
                  : "Review every operations section first"
              }
            >
              Finish &amp; open dashboard
              <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
// Step panels — kept inline to avoid a dozen tiny files for the
// Phase-1 wizard. Later phases will warrant extraction.
// ───────────────────────────────────────────────────────────────────
function TextField({ label, value, onChange, testid, required, placeholder, type = "text", hint }) {
  return (
    <div>
      <Label className="text-xs text-slate-300 uppercase tracking-wide">
        {label}
        {required && <span className="text-rose-400 ml-0.5">*</span>}
      </Label>
      <Input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="bg-slate-800 border-slate-700 text-slate-100 placeholder:text-slate-500"
      />
      {hint && <p className="text-[11px] text-slate-500 mt-0.5">{hint}</p>}
    </div>
  );
}

function BusinessStep({ form, upd }) {
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold flex items-center gap-2">
        <Building2 className="h-5 w-5 text-emerald-400" /> Business letterhead
      </h2>
      <p className="text-xs text-slate-400 -mt-2">
        Appears on every invoice, letter and emailed quote. You can tweak
        later from Settings → Integrations.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TextField
          label="Trading name"
          value={form.trading_name}
          onChange={(v) => upd("trading_name", v)}
          testid="ob-trading-name"
          placeholder="Smith Towing Ltd"
          required
        />
        <TextField
          label="Trading as (optional)"
          value={form.trading_as}
          onChange={(v) => upd("trading_as", v)}
          testid="ob-trading-as"
          placeholder="T/A Smith Rescue"
        />
        <TextField
          label="Street address"
          value={form.address_line1}
          onChange={(v) => upd("address_line1", v)}
          testid="ob-address-1"
          placeholder="12 Example Road"
          required
        />
        <TextField
          label="Suburb / City"
          value={form.address_line2}
          onChange={(v) => upd("address_line2", v)}
          testid="ob-address-2"
          placeholder="Suburb, city"
        />
        <TextField
          label="Ops phone (mobile)"
          value={form.phone_primary}
          onChange={(v) => upd("phone_primary", v)}
          testid="ob-phone-1"
          placeholder="021 234 5678"
          required
        />
        <TextField
          label="Ops phone (landline)"
          value={form.phone_secondary}
          onChange={(v) => upd("phone_secondary", v)}
          testid="ob-phone-2"
          placeholder="03 123 4567"
        />
        <TextField
          label="Email"
          type="email"
          value={form.email}
          onChange={(v) => upd("email", v)}
          testid="ob-email"
          placeholder="ops@smithtowing.co.nz"
          required
        />
        <TextField
          label="GST number"
          value={form.gst_number}
          onChange={(v) => upd("gst_number", v)}
          testid="ob-gst-number"
          placeholder="123-456-789"
          hint="Leave blank if not GST-registered."
        />
      </div>
    </div>
  );
}

function DepotStep({ value, onChange }) {
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold flex items-center gap-2">
        <MapPin className="h-5 w-5 text-emerald-400" /> Depot address
      </h2>
      <p className="text-xs text-slate-400 -mt-2">
        Where your trucks start and finish. We use it as the start/end of every
        round-trip quote and the centre of your dispatch map.
      </p>
      <LocationPicker
        value={value}
        onChange={onChange}
        placeholder="Where do you dispatch from?"
        testIdPrefix="ob-depot"
      />
    </div>
  );
}

function YardsStep({ yards, onChange, depot }) {
  const set = (i, v) => onChange(yards.map((y, idx) => (idx === i ? v : y)));
  const add = () =>
    onChange([...yards, { name: `Yard ${yards.length + 1}`, address: "", lat: null, lng: null }]);
  const remove = (i) => onChange(yards.filter((_, idx) => idx !== i));
  const copyDepot = (i) =>
    set(i, { ...yards[i], address: depot.address, lat: depot.lat, lng: depot.lng });

  return (
    <div className="space-y-5">
      <h2 className="text-lg font-bold flex items-center gap-2">
        <Warehouse className="h-5 w-5 text-emerald-400" /> Storage yards
      </h2>
      <p className="text-xs text-slate-400 -mt-2">
        Where you store impounded and accident vehicles. Pinning the exact gate
        lets us start the storage clock automatically when a truck arrives, and
        measure storage tow km properly. Up to two yards — skip this if you
        don't store vehicles.
      </p>
      {yards.map((y, i) => (
        <div
          key={i}
          className="rounded-md border border-slate-800 bg-slate-950/40 p-4 space-y-3"
          data-testid={`ob-yard-${i}`}
        >
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label className="text-xs text-slate-300 uppercase tracking-wide">
                Yard name
              </Label>
              <Input
                value={y.name || ""}
                onChange={(e) => set(i, { ...y, name: e.target.value })}
                placeholder="Main yard"
                data-testid={`ob-yard-${i}-name`}
                className="bg-slate-800 border-slate-700 text-slate-100 placeholder:text-slate-500"
              />
            </div>
            {yards.length > 1 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => remove(i)}
                data-testid={`ob-yard-${i}-remove`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
          <LocationPicker
            value={y}
            onChange={(v) => set(i, { ...y, ...v })}
            placeholder="Yard street address"
            testIdPrefix={`ob-yard-${i}`}
          />
          {depot?.lat != null && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => copyDepot(i)}
              data-testid={`ob-yard-${i}-same-as-depot`}
            >
              Same as my depot
            </Button>
          )}
        </div>
      ))}
      {yards.length < 2 && (
        <Button
          type="button"
          variant="secondary"
          onClick={add}
          data-testid="ob-yard-add"
        >
          <Plus className="h-4 w-4" />
          Add a second yard
        </Button>
      )}
    </div>
  );
}

function TruckStep({ form, upd }) {
  const capacities = ["Standard Tow", "Heavy Duty", "Flatbed"];
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold flex items-center gap-2">
        <TruckIcon className="h-5 w-5 text-emerald-400" /> Your first truck
      </h2>
      <p className="text-xs text-slate-400 -mt-2">
        One truck is enough to go live — add the rest from Fleet later.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TextField
          label="Truck name"
          value={form.name}
          onChange={(v) => upd("name", v)}
          testid="ob-truck-name"
          placeholder="Truck 1 — Isuzu Slide"
          required
        />
        <TextField
          label="Driver callsign"
          value={form.driver}
          onChange={(v) => upd("driver", v)}
          testid="ob-truck-driver"
          placeholder="Bob S."
          required
        />
        <TextField
          label="Driver phone (optional)"
          value={form.phone}
          onChange={(v) => upd("phone", v)}
          testid="ob-truck-phone"
          placeholder="021 234 5678"
        />
        <div>
          <Label className="text-xs text-slate-300 uppercase tracking-wide">
            Capacity
          </Label>
          <select
            value={form.capacity}
            onChange={(e) => upd("capacity", e.target.value)}
            data-testid="ob-truck-capacity"
            className="w-full rounded-md bg-slate-800 border border-slate-700 text-slate-100 h-10 px-3 text-sm"
          >
            {capacities.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
